import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import DeveloperPanels from "@/components/marketing/DeveloperPanels";
import { BODY_SMALL, HEADING_2 } from "@/components/marketing/marketingType";
import { MID } from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import { LandingSection } from "../LandingPrimitives";
import { sectionPad } from "./landingRun";

type Developer = Laid<"developer">;

/**
 * `.why-web_dot-wrap`: the dotted rules either side of the band, dropped below
 * the card step.
 */
const DOT_WRAP = {
  display: "flex",
  flexDirection: "column",
  borderLeft: "1px dotted #B6B8C3",
  borderRight: "1px dotted #B6B8C3",
  [MID]: { borderLeft: "none", borderRight: "none" },
} satisfies SxProps<Theme>;

/** `.why-web_item.is-left_align`: the heading over the panels, inset 32 until the card step. */
const ITEM = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "32px",
  px: "32px",
  [MID]: { gap: "24px", px: 0 },
} satisfies SxProps<Theme>;

/** `.why-web-title_wrap`: the heading and its line, left, on a 25.3rem measure. */
const TITLE_WRAP = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  width: 1,
  maxWidth: "25.3rem",
  textAlign: "left",
} satisfies SxProps<Theme>;

/** A band link as the panels take it. */
function panelCta(link: { label?: string; href?: string } | undefined) {
  return { label: link?.label ?? "", href: link?.href ?? "" };
}

/** The integrations' rows, each mark flagged where the page draws it larger. */
function logoRows(block: Developer) {
  const wide = new Set(
    (block.wideLogos ?? []).map(([row, index]) => `${row}:${index}`)
  );
  return (block.integrations?.rows ?? []).map((row, rowIndex) =>
    (row.logos ?? []).map((src, index) => ({
      src,
      wide: wide.has(`${rowIndex}:${index}`),
    }))
  );
}

/**
 * The developer band a landing page publishes: the API card and the scrolling
 * integrations, drawn by the same panels as the home page's.
 */
export default function DeveloperBandBlock({ block }: { block: Developer }) {
  return (
    <LandingSection padding={block.pad ? sectionPad(block.pad) : "both"}>
      <Box sx={DOT_WRAP}>
        <Box sx={ITEM}>
          <Box sx={TITLE_WRAP}>
            <Typography component="h2" sx={HEADING_2}>
              {block.title}
            </Typography>
            {block.body ? (
              <Typography sx={BODY_SMALL}>{block.body}</Typography>
            ) : null}
          </Box>
          <DeveloperPanels
            apiCard={block.api?.image ?? ""}
            apiCta={panelCta(block.api?.link)}
            integrationsCta={panelCta(block.integrations?.link)}
            rows={logoRows(block)}
          />
        </Box>
      </Box>
    </LandingSection>
  );
}
